import type { NonNullableFormBuilder } from '@angular/forms';
import type { OnyxSetOptionalNullDeep } from '@onyx/angular';
import type { OmitDeep, Paths } from 'type-fest';
import { CountriesWhitelistFormComponent } from '../../../../common/components/forms/countries-whitelist-form/countries-whitelist-form.component';
import { NoteFormComponent } from '../../../../common/components/forms/note-form/note-form.component';
import { ParametersFormComponent } from '../../../../common/components/forms/parameters-form/parameters-form.component';
import type { PreferencesService } from '../../../../common/services/preferences.service';
import { FleetCargoSpaceFormComponent } from '../../fleet-form/fleet-cargo-space-form/fleet-cargo-space-form.component';
import { FleetCo2EmissionsFormComponent } from '../../fleet-form/fleet-co2-emissions-form/fleet-co2-emissions-form.component';
import { FleetFuelTankCapacityFormComponent } from '../../fleet-form/fleet-fuel-tank-capacity-form/fleet-fuel-tank-capacity-form.component';
import { FleetGeneralInformationFormComponent } from '../../fleet-form/fleet-general-information-form/fleet-general-information-form.component';
import { FleetInsuranceFormComponent } from '../../fleet-form/fleet-insurance-form/fleet-insurance-form.component';
import { FleetLicensesAndPermitsFormComponent } from '../../fleet-form/fleet-licenses-and-permits-form/fleet-licenses-and-permits-form.component';
import { FleetOwnershipFormComponent } from '../../fleet-form/fleet-ownership-form/fleet-ownership-form.component';
import { FleetRegistrationCertificateFormComponent } from '../../fleet-form/fleet-registration-certificate-form/fleet-registration-certificate-form.component';
import { FleetTachographFormComponent } from '../../fleet-form/fleet-tachograph-form/fleet-tachograph-form.component';
import { FleetVehicleMeasurementsFormComponent } from '../../fleet-form/fleet-vehicle-measurements-form/fleet-vehicle-measurements-form.component';
import type { Fleet } from '../interfaces/fleet';

export type FleetFormGroup = ReturnType<typeof FleetFormHelper.buildForm>;
export type FleetForm = ReturnType<FleetFormGroup['getRawValue']>;

export const FLEET_UTILITY_KEYS = [
  'licensesAndPermits.hasAbstract',
] as const satisfies Paths<FleetForm>[];

export type FleetFormDto = OnyxSetOptionalNullDeep<
  OmitDeep<FleetForm, (typeof FLEET_UTILITY_KEYS)[number]>,
  | 'cargoSpace'
  | 'fuelTankCapacity'
  | 'ownership.vehicleValue'
  | 'ownership.selfDeposit'
  | 'ownership.buyout'
  | 'ownership.installment'
  | 'licensesAndPermits'
  | 'tachograph'
  | 'co2Emissions'
  | 'comprehensiveInsurance'
>;

export class FleetFormHelper {
  public static fromDto(dto: Fleet): FleetForm {
    return {
      ...dto,
      generalInformation: {
        ...dto.generalInformation,
        assignedBase: dto.generalInformation.assignedBase?.uuid ?? null,
      },
      cargoSpace: {
        ...dto.cargoSpace,
        ...dto.cargoSpace.dimensions,
      },
      fuelTankCapacity: dto.fuelTankCapacity as FleetForm['fuelTankCapacity'],
      ownership: {
        ...dto.ownership,
        vehicleValue: dto.ownership
          .vehicleValue as FleetForm['ownership']['vehicleValue'],
        leasingProfile: null,
        loanProfile: null,
        rentalProfile: null,
        selfDeposit: dto.ownership
          .selfDeposit as FleetForm['ownership']['selfDeposit'],
        buyout: dto.ownership.buyout as FleetForm['ownership']['buyout'],
        installment: dto.ownership
          .installment as FleetForm['ownership']['installment'],
      },
      licensesAndPermits:
        dto.licensesAndPermits as FleetForm['licensesAndPermits'],
      allowedCountries: dto.allowedCountries as FleetForm['allowedCountries'],
      additionalParameters: {
        ...dto.additionalParameters,
        temperatureRange: dto.additionalParameters
          .temperatureRange as FleetForm['additionalParameters']['temperatureRange'],
        otherVehicleParameters:
          dto.additionalParameters.otherVehicleParameters?.map(
            (parameter) => parameter.uuid,
          ) ?? null,
      },
      note: {
        content: dto.note?.content ?? null,
      },
      tachograph: dto.tachograph as FleetForm['tachograph'],
      registrationCertificate:
        dto.registrationCertificate as FleetForm['registrationCertificate'],
      co2Emissions: dto.co2Emissions as FleetForm['co2Emissions'],
      thirdPartyLiabilityInsurance:
        dto.thirdPartyLiabilityInsurance as FleetForm['thirdPartyLiabilityInsurance'],
      comprehensiveInsurance:
        dto.comprehensiveInsurance as FleetForm['comprehensiveInsurance'],
    };
  }

  public static buildForm(
    fb: NonNullableFormBuilder,
    preferencesService: PreferencesService,
  ) {
    return fb.group({
      generalInformation: FleetGeneralInformationFormComponent.buildForm(
        fb,
        preferencesService,
      ),
      vehicleMeasurements: FleetVehicleMeasurementsFormComponent.buildForm(fb),
      cargoSpace: FleetCargoSpaceFormComponent.buildForm(fb),
      fuelTankCapacity: FleetFuelTankCapacityFormComponent.buildForm(
        fb,
        preferencesService,
      ),
      ownership: FleetOwnershipFormComponent.buildForm(fb, preferencesService),
      licensesAndPermits: FleetLicensesAndPermitsFormComponent.buildForm(
        fb,
        preferencesService,
      ),
      allowedCountries: CountriesWhitelistFormComponent.buildForm(fb),
      note: NoteFormComponent.buildForm(fb),
      additionalParameters: ParametersFormComponent.buildForm('fleet', fb),
      tachograph: FleetTachographFormComponent.buildForm(fb),
      registrationCertificate:
        FleetRegistrationCertificateFormComponent.buildForm(fb),
      co2Emissions: FleetCo2EmissionsFormComponent.buildForm(fb),
      thirdPartyLiabilityInsurance: FleetInsuranceFormComponent.buildForm(
        fb,
        preferencesService,
      ),
      comprehensiveInsurance: FleetInsuranceFormComponent.buildForm(
        fb,
        preferencesService,
      ),
    });
  }
}
